import React, { useEffect, useState, useCallback } from "react";
import { FiSearch, FiImage, FiCamera, FiX, FiChevronRight, FiChevronLeft } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import SectionTitle from "../components/SectionTitle";
import Lightbox from "../components/Lightbox";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import OmSymbol from "../components/OmSymbol";
import { SAURABH } from "../utils/siteData";
import { buildWhatsAppLink } from "../utils/whatsapp";
import api, { imgUrl } from "../utils/api";

const CATEGORIES = [
  "All",
  "Pooja Ceremony",
  "Wedding / Vivah",
  "Griha Pravesh",
  "Navchandi Yag",
  "Astrology Session",
  "Festival",
  "Other",
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Lightbox state
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const fetchItems = useCallback(() => {
    setLoading(true);
    api
      .get("/gallery", { params: { category, search, page, limit: 12 } })
      .then((res) => {
        setItems(res.data.items);
        setPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, search, page]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setPage(1);
  }, [category, search]);

  const openLightbox = (galleryItem, imageIndex = 0) => {
    const imgs = (galleryItem.images || []).map((src, i) => ({
      src,
      alt: `${galleryItem.title} — photo ${i + 1}`,
    }));
    setLightboxImages(imgs);
    setLightboxIndex(imageIndex);
    setLightboxOpen(true);
  };

  const totalPhotos = items.reduce((sum, item) => sum + (item.images?.length || 0), 0);
  const bookLink = buildWhatsAppLink(SAURABH.whatsapp, "Namaste 🙏, I saw your gallery and would like to book a pooja.");

  return (
    <div>
      <SEO
        title="Photo Gallery — Pooja Ceremonies & Events"
        description="Browse photos of authentic Vedic pooja ceremonies, weddings, griha pravesh and festivals performed by Pandit Saurabh Shastri in Thane and Mumbai."
        keywords="pooja photos thane, wedding photos pandit, griha pravesh photos, pandit gallery thane"
        path="/gallery"
      />

      <PageHero
        eyebrow="Photo Gallery"
        title="Sacred Moments & Ceremonies"
        subtitle="A visual journey through authentic Vedic poojas, weddings, and rituals performed with complete devotion for families across Thane & Mumbai."
      />

      {/* Stats strip — same style as Marketplace trust band */}
      <section className="bg-maroon-900 bg-mandala text-white">
        <div className="max-w-7xl mx-auto px-4 relative z-10 grid grid-cols-3 divide-x divide-gold-900/40">
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">{total}</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Ceremonies</span>
          </div>
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">{CATEGORIES.length - 1}</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Categories</span>
          </div>
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">{totalPhotos}+</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Photos</span>
          </div>
        </div>
      </section>

      <section className="py-14 bg-saffron-50/60">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Browse Ceremonies"
            title="Ceremony Gallery"
            subtitle="Explore our collection of sacred ceremonies, weddings, and festivals — each performed with authentic Vedic traditions."
          />

          {/* FILTERS — same pattern as Marketplace */}
          <div className="flex flex-col lg:flex-row gap-4 justify-between mb-8">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    category === cat
                      ? "bg-saffron-500 text-white border-saffron-500 shadow-gold"
                      : "bg-white text-gray-700 border-gold-200 hover:border-saffron-400 hover:text-maroon-700"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ceremonies..."
                className="w-full pl-10 pr-9 py-2.5 rounded-full border border-gold-200 bg-white focus:outline-none focus:ring-2 focus:ring-saffron-400 input-premium"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
          </div>

          {/* RESULTS */}
          {loading ? (
            <Loader label="Loading gallery..." />
          ) : items.length === 0 ? (
            <EmptyState
              title="No photos yet"
              subtitle="Gallery is being updated. Please check back soon for photos of our ceremonies and events."
            />
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-5">
                Showing <span className="font-semibold text-maroon-700">{items.length}</span> of{" "}
                <span className="font-semibold text-maroon-700">{total}</span> ceremonies
              </p>

              {/* Grid — same card style as Marketplace: hover-lift, gold-corner, shadow-premium */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {items.map((item, i) => (
                  <motion.div
                    key={item._id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.45, delay: (i % 4) * 0.07 }}
                    className="group relative hover-lift gold-corner bg-white rounded-2xl shadow-premium border border-gold-100 overflow-hidden flex flex-col h-full cursor-pointer"
                    onClick={() => openLightbox(item, 0)}
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] bg-gradient-to-br from-saffron-50 to-gold-100/60 overflow-hidden">
                      {item.images && item.images[0] ? (
                        <img
                          src={imgUrl(item.images[0])}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                          <FiCamera size={44} className="text-saffron-300" />
                          <span className="text-xs text-saffron-400 font-medium">{item.category}</span>
                        </div>
                      )}

                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/70 via-maroon-900/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />

                      {/* Category badge — top left */}
                      <span className="absolute top-2.5 left-2.5 bg-white/90 backdrop-blur-sm text-saffron-700 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
                        {item.category}
                      </span>

                      {/* Photo count badge — top right */}
                      {item.images && item.images.length > 1 && (
                        <div className="absolute top-2.5 right-2.5 bg-black/60 backdrop-blur-sm text-white text-[11px] px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                          <FiImage size={12} /> {item.images.length}
                        </div>
                      )}

                      {/* Hover: "View Photos" on image */}
                      <div className="absolute bottom-3 left-3 right-3 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 flex items-center gap-1.5 text-saffron-300 text-sm font-semibold">
                        View Photos <FiChevronRight size={16} />
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-5 flex flex-col flex-grow">
                      <h4 className="font-heading text-maroon-800 text-sm sm:text-base leading-tight mb-1.5 line-clamp-2 group-hover:text-saffron-600 transition-colors">
                        {item.title}
                      </h4>
                      {item.caption && (
                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.caption}</p>
                      )}

                      {/* Thumbnail strip */}
                      {item.images && item.images.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-gold-100/80 flex items-center gap-1.5 overflow-hidden">
                          {item.images.slice(0, 4).map((img, idx) => (
                            <img
                              key={idx}
                              src={imgUrl(img)}
                              alt={`${item.title} thumb ${idx + 1}`}
                              className="w-9 h-9 rounded-lg object-cover border border-gold-200/60 hover:border-saffron-400 transition-colors"
                              loading="lazy"
                              onClick={(e) => {
                                e.stopPropagation();
                                openLightbox(item, idx);
                              }}
                            />
                          ))}
                          {item.images.length > 4 && (
                            <span className="text-[11px] text-gray-400 font-medium ml-1">
                              +{item.images.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium bg-white border border-gold-200 text-gray-700 hover:border-saffron-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiChevronLeft size={16} /> Prev
                  </button>
                  {[...Array(pages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                        page === idx + 1
                          ? "bg-maroon-700 text-gold-100"
                          : "bg-white border border-gold-200 text-gray-700 hover:border-saffron-400"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page === pages}
                    className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium bg-white border border-gold-200 text-gray-700 hover:border-saffron-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next <FiChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA banner — same style as Marketplace help strip */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-3xl bg-hero-gradient bg-mandala text-white p-8 sm:p-10 text-center relative overflow-hidden shadow-premium-lg">
            <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
            <div className="relative">
              <span className="text-3xl inline-flex justify-center text-gold-300"><OmSymbol size={32} /></span>
              <h3 className="font-heading text-2xl sm:text-3xl mt-2 mb-2">Want a Ceremony Like These?</h3>
              <p className="text-gold-100/85 max-w-xl mx-auto mb-6">
                Book an authentic Vedic pooja or ceremony with Pandit Saurabh Shastri — performed with the same devotion you see here.
              </p>
              <a
                href={bookLink}
                target="_blank"
                rel="noreferrer"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
              >
                <FaWhatsapp /> Book on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox overlay */}
      {lightboxOpen && (
        <Lightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
